/**
 * Instalar um plugin web (docs/spec/web-plugins.md, "Como se usa"): "Instalar plugin…" abre o diálogo
 * nas três origens, os plugins oficiais do catálogo, o Desk Cloud de quem instala e o computador. Um
 * arquivo é lido e conferido aqui; a confirmação mostra o plugin e os problemas; ao confirmar, o
 * pacote vai para o Desk Cloud da mesa (um .zip que já está lá vai direto) e o servidor confere de
 * novo e instala. Um oficial instala direto da lista, sem arquivo de quem instala.
 */
import { create } from 'zustand';
import { api } from '../../api.js';
import { fileUrl } from '../../files.js';
import { translate, useLanguage } from '../../i18n/index.js';
import { uploadRefusal } from '../../plans/planModel.js';
import type { DeskFile } from '../../shared.js';
import { useApp } from '../../store.js';
import { inspectPackage, langOf, type Inspection } from './sdk.js';

export type InstallTab = 'official' | 'cloud' | 'computer';

/** O arquivo conferido, esperando a confirmação. `cloudFileId`: veio do Desk Cloud (um .zip vai direto). */
export type PendingInstall = { fileName: string; inspection: Inspection; cloudFileId?: string };

type InstallState = {
  /** o diálogo está aberto (nas origens, ou na confirmação) */
  open: boolean;
  tab: InstallTab;
  /** a confirmação de um arquivo; null nas origens */
  pending: PendingInstall | null;
  /** a confirmação chegou pelas origens (tem "Voltar") */
  fromSources: boolean;
  /** instalando o arquivo da confirmação */
  busy: boolean;
  /** o arquivo marcado na aba Desk Cloud (cada abertura começa sem nenhum) */
  cloudChoice: DeskFile | null;
  show: (tab?: InstallTab) => void;
  chooseCloud: (file: DeskFile | null) => void;
  setTab: (tab: InstallTab) => void;
  confirm: (pending: PendingInstall) => void;
  back: () => void;
  close: () => void;
  setBusy: (busy: boolean) => void;
};

export const useWebInstall = create<InstallState>()((set) => ({
  open: false,
  tab: 'official',
  pending: null,
  fromSources: false,
  busy: false,
  cloudChoice: null,
  show: (tab = 'official') => set({ open: true, tab, pending: null, fromSources: false, busy: false, cloudChoice: null }),
  chooseCloud: (cloudChoice) => set({ cloudChoice }),
  setTab: (tab) => set({ tab }),
  confirm: (pending) => set((state) => ({ open: true, pending, fromSources: state.open && !state.pending, busy: false })),
  back: () => set({ pending: null, busy: false }),
  close: () => set({ open: false, pending: null, fromSources: false, busy: false, cloudChoice: null }),
  setBusy: (busy) => set({ busy }),
}));

/** Abre "Instalar plugin…" numa origem (os oficiais, se nada for dito). */
export function openPluginInstall(tab?: InstallTab): void {
  useWebInstall.getState().show(tab);
}

const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

/** Confere os bytes de um pacote, com os problemas na língua da interface (o relatório vai para a IA nela). */
export function inspectPluginBytes(name: string, bytes: Uint8Array): Inspection {
  return inspectPackage(name, bytes, { lang: langOf(useLanguage.getState().language) });
}

/** Lê e confere o arquivo do computador e mostra a confirmação. */
export async function readPluginFile(file: File): Promise<void> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  useWebInstall.getState().confirm({ fileName: file.name, inspection: inspectPluginBytes(file.name, bytes) });
}

/** Os bytes de um arquivo do Desk Cloud, pelo link de leitura. */
export async function cloudFileBytes(file: DeskFile): Promise<Uint8Array> {
  const response = await fetch(await fileUrl(file.id), { cache: 'no-store' });
  if (!response.ok) throw new Error(say('webInstall.cloudFailed', { file: file.name }));
  return new Uint8Array(await response.arrayBuffer());
}

/** Baixa e confere um arquivo do Desk Cloud e mostra a confirmação. */
export async function readCloudPlugin(file: DeskFile): Promise<void> {
  const bytes = await cloudFileBytes(file);
  useWebInstall.getState().confirm({ fileName: file.name, inspection: inspectPluginBytes(file.name, bytes), cloudFileId: file.id });
}

/** Envia o pacote (se precisar) e pede a instalação; devolve o erro, ou null. */
export async function sendWebInstall(pending: PendingInstall): Promise<string | null> {
  const pkg = pending.inspection.pkg;
  const state = useApp.getState();
  if (!pkg) return say('webInstall.hasErrors');
  if (!state.token || !state.activeRoomId) return say('webInstall.noRoom');
  // um .zip do Desk Cloud já está lá: o servidor o desempacota como foi conferido, sem cópia nova
  // (docs/spec/web-plugins.md). Um .html vira .zip aqui e é enviado como os do computador
  if (pending.cloudFileId && pkg.format === 'web-zip') {
    const result = await state.installWebPlugin(pending.cloudFileId);
    return result.ok ? null : (result.error ?? say('webInstall.failed'));
  }
  // o pacote vai para os arquivos da mesa: avisa antes de enviar o que o servidor recusaria (docs/spec/planos.md)
  const refusal = uploadRefusal(state, pkg.zip.length, (key, params) => say(key, params));
  if (refusal) return refusal;
  const name = `${pkg.manifest.id}-${pkg.manifest.version}.zip`;
  const { file } = await api.uploadFile(state.token, new File([pkg.zip as BlobPart], name, { type: 'application/zip' }), { roomId: state.activeRoomId });
  const result = await state.installWebPlugin(file.id);
  return result.ok ? null : (result.error ?? say('webInstall.failed'));
}
